/**
 * AutoFixStatusChip — small floating chip that surfaces the self-heal
 * engine's activity so the user can visually confirm bugs are being
 * auto-resolved in the background.
 *
 * Data source: /api/auto-improve/runtime-flags (polled every 60 s via
 * useAutoImproveFlags). Only rendered for signed-in users so guests
 * don't see it on marketing pages.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck } from "lucide-react";
import { useAutoImproveFlags } from "@/lib/useAutoImproveFlags";
import { useAuth } from "@/lib/auth";

export default function AutoFixStatusChip() {
  const flags = useAutoImproveFlags();
  const { user } = useAuth() || {};

  const summary = useMemo(() => {
    const entries = Object.entries(flags || {});
    const suppressCount = entries.filter(([k]) => k.startsWith("suppress:")).length;
    const roadmapCount = entries.filter(([k]) => k.startsWith("roadmap_tease:")).length;
    const total = entries.length;
    return { total, suppressCount, roadmapCount };
  }, [flags]);

  if (!user) return null;
  // Admin-only visibility. Regular landlords / tenants shouldn't be
  // distracted by internal engine telemetry — they still benefit from
  // the auto-suppress flags via the runtime-flags feed.
  if ((user.role || "").toLowerCase() !== "admin") return null;
  if (!summary.total) return null;

  return (
    <Link
      to={"/admin/self-heal"}
      data-testid="auto-fix-status-chip"
      className="fixed bottom-20 left-4 z-30 flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-medium shadow-lg hover:bg-emerald-700 transition-colors"
      title={`Auto-fix engine active · ${summary.suppressCount} auto-suppress · ${summary.roadmapCount} roadmap`}
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
      </span>
      <ShieldCheck className="h-3.5 w-3.5" />
      <span>Auto-fix engine · {summary.total} active</span>
    </Link>
  );
}
